/**
 * The branding to actually show for the current business, everywhere the portal shell renders it
 * (PHP-3875): a thin wrapper over the Settings → Branding API (`portal.branding` / `portal.brandingLogo`,
 * PHP-3378) that applies the same two guards the backend applies when it resolves branding for an e-mail
 * or an invoice — a colour that doesn't read clearly is never shown, and a logo that fails to load falls
 * back to the platform mark — so the shell, e-mails and invoices all fall back the same way.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { checkContrast } from "@/lib/contrast";
import { portal } from "@/lib/api";

/** Mirrors the backend's default (`app.modules.business_branding.models.DEFAULT_COLOUR_HEX`): the
 * shell's own coral accent, shown whenever a business has no safe colour of its own. */
export const DEFAULT_BRAND_COLOUR = "#b23117";

export interface EffectiveBranding {
  /** Always a safe, 6-digit hex colour: the business's own when it passes WCAG AA, the default otherwise. */
  colourHex: string;
  /** `null` when there's no logo to show (none uploaded, still loading, or it failed to load). */
  logoUrl: string | null;
  /** True when `colourHex` is the platform default, not the business's own. */
  usesDefaultColour: boolean;
  /** True when `logoUrl` is `null` because there's nothing (safe) of the business's own to show. */
  usesDefaultLogo: boolean;
}

/** `orgId` may be `null` while the active organization is still resolving. */
export function useEffectiveBranding(orgId: string | null | undefined): EffectiveBranding {
  const branding = useQuery({
    queryKey: ["branding", orgId],
    queryFn: () => portal.branding(orgId as string),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
  const hasLogo = Boolean(branding.data?.logo);
  const logo = useQuery({
    queryKey: ["branding-logo", orgId, branding.data?.version],
    queryFn: () => portal.brandingLogo(orgId as string),
    enabled: Boolean(orgId) && hasLogo,
    staleTime: 60_000,
  });
  const blob = logo.data instanceof Blob ? logo.data : null;
  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const colourHex = branding.data?.colour_hex ?? null;
  const contrast = colourHex ? checkContrast(colourHex) : null;
  const safeColourHex = contrast?.passes_aa ? contrast.colour_hex : null;

  return {
    colourHex: safeColourHex ?? DEFAULT_BRAND_COLOUR,
    usesDefaultColour: !safeColourHex,
    logoUrl: logo.isError ? null : objectUrl,
    usesDefaultLogo: logo.isError || !objectUrl,
  };
}
